import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { authApi } from '../../api/auth';
import { Mail, Lock, User, Gift } from 'lucide-react';
import Logo from '../Logo';
import '../../styles/auth-gaming.css';
import { useLanguage } from '../../i18n/LanguageContext';

const REQUIRE_EMAIL_VERIFICATION = process.env.REACT_APP_REQUIRE_EMAIL_VERIFICATION === 'true';

const Register = () => {
  const location = useLocation();
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
    referralCode: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const rawReferral = params.get('ref') || params.get('referral') || params.get('referralCode');
    const referralFromUrl = rawReferral?.trim();

    if (referralFromUrl) {
      setFormData((prev) => (
        prev.referralCode
          ? prev
          : { ...prev, referralCode: referralFromUrl }
      ));
    }
  }, [location.search]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    // Validation
    if (!formData.username || !formData.email || !formData.password) {
      setError(t('auth.fillAllFields'));
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError(t('auth.passwordMismatch'));
      return;
    }

    if (formData.password.length < 6) {
      setError(t('auth.passwordShort'));
      return;
    }

    setLoading(true);

    try {
      const response = await authApi.register(formData);
      
      if (response?.success) {
        setSuccess(t(REQUIRE_EMAIL_VERIFICATION ? 'auth.registrationPending' : 'auth.registrationSuccess'));
      }
    } catch (err) {
      setError(err.message || t('auth.registrationFailed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container">
      {/* Auth Background */}
      <div 
        className="auth-background"
        style={{
          backgroundImage: `linear-gradient(180deg, rgba(0, 0, 0, 0.4) 0%, rgba(0, 0, 0, 0.6) 100%), url('/images/login.png')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center top',
          backgroundRepeat: 'no-repeat'
        }}
      ></div>
      
      <div className="w-full max-w-md relative z-10">
        {/* Logo/Header */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <Logo size="lg" showText={false} />
          </div>
          <h1 className="text-4xl font-bold mb-2">DIOS10</h1>
          <p>Football Fantasy Game</p>
        </div>

        {/* Registration Form */}
        <div className="football-card glass-football auth-card-warm p-8">
          <h2 className="text-2xl font-bold mb-6 text-center">{t('auth.createAccount')}</h2>

          {error && (
            <div className="bg-red-50 border border-red-200 p-4 mb-6">
              <p className="text-red-800 text-sm">{error}</p>
            </div>
          )}
          {success && (
            <div className="bg-green-50 border border-green-200 p-4 mb-6">
              <p className="text-green-800 text-sm">{success}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block mb-2 flex items-center">
                <User className="w-4 h-4 mr-2" />
                {t('auth.username')} *
              </label>
              <input
                type="text"
                value={formData.username}
                onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                className="w-full"
                placeholder={t('auth.chooseUsername')}
                required
              />
            </div>

            <div>
              <label className="block mb-2 flex items-center">
                <Mail className="w-4 h-4 mr-2" />
                {t('auth.email')} *
              </label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full"
                placeholder="your.email@example.com"
                required
              />
            </div>

            <div>
              <label className="block mb-2 flex items-center">
                <Lock className="w-4 h-4 mr-2" />
                {t('auth.password')} *
              </label>
              <input
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className="w-full"
                placeholder={t('auth.minimumCharacters')}
                required
              />
            </div>

            <div>
              <label className="block mb-2 flex items-center">
                <Lock className="w-4 h-4 mr-2" />
                {t('auth.confirmPassword')} *
              </label>
              <input
                type="password"
                value={formData.confirmPassword}
                onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                className="w-full"
                placeholder={t('auth.confirmPasswordPlaceholder')}
                required
              />
            </div>

            <div>
              <label className="block mb-2 flex items-center">
                <Gift className="w-4 h-4 mr-2" />
                {t('auth.referralCode')}
              </label>
              <input
                type="text"
                value={formData.referralCode}
                onChange={(e) => setFormData({ ...formData, referralCode: e.target.value })}
                className="w-full"
                placeholder={t('auth.referralPlaceholder')}
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="btn-football w-full text-lg py-3 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? t('auth.creatingAccount') : t('auth.startJourney')}
            </button>
          </form>

          <div className="mt-6 text-center">
            <p>
              {t('auth.alreadyAccount')}{' '}
              <Link to="/login" className="font-semibold">
                {t('auth.signIn')}
              </Link>
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center mt-6 text-sm">
          <p>{t('auth.terms')}</p>
        </div>
      </div>
    </div>
  );
};

export default Register;

